// src/App.tsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { LoginForm } from './components/login/LoginForm';
import { OAuthCallback } from './components/oauth/OAuthCallback';
import HoldingsHistory from './components/13FQoqHistory/HoldingsHistory';
import { SANDBOX_AUTOLOGIN, SANDBOX_EMAIL, SANDBOX_PASSWORD } from './config/env';

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const { isAuthenticated, signInLocal } = useContext(AuthContext);
    const location = useLocation();

    // Sandbox: do a silent preset login on first hit, render a neutral splash meanwhile.
    const [, setBooting] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        if (!SANDBOX_AUTOLOGIN) return;
        if (isAuthenticated) return;
        if (started.current) return;
        started.current = true;
        setBooting(true);

        (async () => {
            try {
                if (!SANDBOX_EMAIL || !SANDBOX_PASSWORD) return; // safety
                await signInLocal(SANDBOX_EMAIL, SANDBOX_PASSWORD);
            } finally {
                setBooting(false);
            }
        })();
    }, [isAuthenticated, signInLocal]);

    if (SANDBOX_AUTOLOGIN) {
        if (isAuthenticated) return children;
        // Neutral dark splash to match app background (prevents login page flash)
        return <div style={{ minHeight: '100vh', background: '#1E1E1E' }} />;
    }

    if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
    return children;
};

export const App: React.FC = () => (
    <AuthProvider>
        <BrowserRouter>
            <Routes>
                <Route path="/oauth2/callback/:provider" element={<OAuthCallback />} />
                <Route path="/login" element={<LoginForm />} />
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute>
                            <HoldingsHistory />
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </BrowserRouter>
    </AuthProvider>
);

export default App;
